import Page from '@/components/Page';
import Header from '@/components/HeaderServer';
import Footer from '@/components/Footer';
import FooterColumn from '@/components/FooterColumn';
import Section from '@/components/Section';
import Grid from '@/components/Grid';
import Box from '@/components/Box';
import RichText from '@/components/RichText';
import Text from '@/components/Text';
import Link from '@/components/Link';
import Stars from '@/components/Stars';
import Image from '@/components/Image';
import Video from '@/components/Video';
import Review from '@/components/Review';
import Icon from '@/components/Icon';
import Button from '@/components/Button';
import Hero from '@/components/Hero';
import PromoHero from '@/components/PromoHero';
import Cards from '@/components/Cards';
import Card from '@/components/Card';
import Tabs from '@/components/Tabs';
import TestimonialCarousel from '@/components/TestimonialCarousel';
import Carousel from '@/components/Carousel';
import StatsRow from '@/components/StatsRow';
import Accordion from '@/components/Accordion';
import ContentImage from '@/components/ContentImage';
import PricingCards from '@/components/PricingCards';
import ComparisonTable from '@/components/ComparisonTable';
import Marquee from '@/components/Marquee';
import ServiceCard from '@/components/ServiceCard';
import Collapsible from '@/components/Collapsible';
import CodeBlock from '@/components/CodeBlock';
import Toggle from '@/components/Toggle';
import ToggleTarget from '@/components/ToggleTarget';
import CarouselControl from '@/components/CarouselControl';
import Tooltip from '@/components/Tooltip';
import SharedBlock from '@/components/SharedBlock';
import HubspotFormModal from '@/components/HubspotFormModal';
import HubspotForm from '@/components/HubspotForm';
import CloseButton from '@/components/CloseButton';
import { apiPlugin, storyblokInit, StoryblokStory } from '@storyblok/react/rsc';
import { pointAppLinksAtEnvironment } from '@/lib/app-links';
export { StoryblokStory };

// Components registered with Storyblok
const components = {
	page: Page,
	header: Header,
	footer: Footer,
	footer_column: FooterColumn,
	section: Section,
	grid: Grid,
	box: Box,
	rich_text: RichText,
	text: Text,
	link: Link,
	stars: Stars,
	image: Image,
	video: Video,
	review: Review,
	icon: Icon,
	button: Button,
	hero: Hero,
	promo_hero: PromoHero,
	cards: Cards,
	card: Card,
	tabs: Tabs,
	tab_item: Tabs,
	testimonial_carousel: TestimonialCarousel,
	testimonial: TestimonialCarousel,
	carousel: Carousel,
	stats_row: StatsRow,
	stat: StatsRow,
	accordion: Accordion,
	accordion_item: Accordion,
	content_image: ContentImage,
	pricing_cards: PricingCards,
	pricing_card: PricingCards,
	comparison_table: ComparisonTable,
	comparison_row: ComparisonTable,
	marquee: Marquee,
	service_card: ServiceCard,
	collapsible: Collapsible,
	code_block: CodeBlock,
	toggle: Toggle,
	toggle_target: ToggleTarget,
	carousel_control: CarouselControl,
	tooltip: Tooltip,
	shared_block: SharedBlock,
	hubspot_form_modal: HubspotFormModal,
	hubspot_form: HubspotForm,
	close_button: CloseButton,
	// A Shared Content story is only rendered as a page in preview, so editors can
	// work on it in the Visual Editor; its body renders just like a page's.
	shared_content: Page,
};

// Preview mode renders Storyblok *draft* content and loads the visual-editor
// bridge. Enable it on the preview/staging domain with STORYBLOK_IS_PREVIEW=true;
// leave it unset/false on the live domain so it serves *published* content with
// no bridge. Single source of truth — pages import this.
export const isPreview = process.env.STORYBLOK_IS_PREVIEW === 'true';

// Relations Storyblok resolves into the story it returns: a Shared Block's stored
// uuid becomes the full Shared Content story. The page hands the same list to the
// Visual Editor bridge, so live edits resolve it too.
export const RESOLVE_RELATIONS = ['shared_block.story'];

// Only the server fetches from Storyblok. On the client we register components
// and load the bridge, but skip apiPlugin so the access token never enters the bundle.
const isServer = typeof window === 'undefined';

export const getStoryblokApi = storyblokInit({
	accessToken: isServer ? process.env.STORYBLOK_DELIVERY_API_TOKEN : undefined,
	use: isServer ? [apiPlugin] : [],
	components,
	bridge: isPreview,
	apiOptions: {
		region: process.env.STORYBLOK_REGION || 'eu',
		cache: { clear: 'auto', cv: 'manual' },
	},
});

async function cacheVersion(sbApi, fetchOptions) {
	try {
		const { data } = await sbApi.get('cdn/spaces/me', {}, fetchOptions);
		return data?.space?.version;
	} catch (err) {
		console.warn(`[storyblok] could not read cache version (${err.message}) — requesting without cv`);
		return undefined;
	}
}

// Fetch a story by slug from the Storyblok API
export async function fetchStory(slug, options = {}) {
	if (isServer && !process.env.STORYBLOK_DELIVERY_API_TOKEN) {
		throw new Error('STORYBLOK_DELIVERY_API_TOKEN is not set — cannot fetch content from Storyblok.');
	}

	const sbApi = getStoryblokApi();
	const { language, version = isPreview ? 'draft' : 'published' } = options;

	// Preview always fetches fresh drafts; live caches published content in Next's
	// Data Cache (revalidating periodically) to cut repeated Storyblok API calls.
	const fetchOptions = isPreview
		? { cache: 'no-store' }
		: { next: { revalidate: 60, tags: ['storyblok'] } };

	// Drafts get a fresh cv each request: without one, Storyblok's CDN keeps
	// serving the draft it cached first, so saved edits only showed after a
	// server restart.
	const cv = version === 'published' ? await cacheVersion(sbApi, fetchOptions) : Date.now();

	const params = {
		version,
		resolve_relations: RESOLVE_RELATIONS.join(','),
		...(cv ? { cv } : {}),
		...(language && language !== 'en' ? { language, fallback_lang: 'en' } : {}),
	};

	try {
		const { data } = await sbApi.get(`cdn/stories/${slug}`, params, fetchOptions);
		return pointAppLinksAtEnvironment(data?.story) || null;
	} catch (err) {
		if (err.status === 404) return null;
		throw err;
	}
}

// Fetch the region config (header + footer) for a specific region
export async function fetchConfig(region, options = {}) {
	return fetchStory(`${region}/config`, options);
}

// Fetch every story matching the params, across all result pages (e.g. a
// folder's pages for the sitemap). Cached like fetchStory.
export async function fetchStories(params = {}) {
	const sbApi = getStoryblokApi();
	const version = isPreview ? 'draft' : 'published';
	const fetchOptions = isPreview
		? { cache: 'no-store' }
		: { next: { revalidate: 60, tags: ['storyblok'] } };
	const cv = version === 'published' ? await cacheVersion(sbApi, fetchOptions) : Date.now();
	return sbApi.getAll('cdn/stories', { version, ...(cv ? { cv } : {}), ...params }, 'stories', fetchOptions);
}
